import type { ContainerType, ContentsResponse } from '../storage/storage-api';

const STORAGE_TOKEN_RE = /^tcvs:(rack|shelf|box|row|batch):([0-7][0-9A-HJKMNP-TV-Z]{25})$/i;
const MAX_STORAGE_SCOPE_ENTITIES = 10_000;

const PLURAL_TYPE: Record<string, ContainerType> = {
  rack: 'racks',
  shelf: 'shelves',
  box: 'boxes',
  row: 'rows',
  batch: 'batches',
};

const CHILD_TYPE: Partial<Record<ContainerType, ContainerType>> = {
  racks: 'shelves',
  shelves: 'boxes',
  boxes: 'rows',
  rows: 'batches',
};

const CONTENT_KIND: Partial<Record<ContainerType, ContentsResponse['kind']>> = {
  racks: 'shelves',
  shelves: 'boxes',
  boxes: 'rows',
  rows: 'batches',
};

export interface ReviewStorageScope {
  type: ContainerType;
  entityId: string;
}

export type ReviewStorageContentsLoader = (
  type: ContainerType,
  entityId: string,
  page?: string,
) => Promise<ContentsResponse>;

export function parseReviewStorageScope(value: string): ReviewStorageScope | undefined {
  let decoded = value.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    return undefined;
  }
  const match = STORAGE_TOKEN_RE.exec(decoded);
  if (match === null) return undefined;
  const tag = match[1]!.toLowerCase();
  const id = match[2]!.toUpperCase();
  return { type: PLURAL_TYPE[tag]!, entityId: `tcvs:${tag}:${id}` };
}

function entityIds(items: unknown[]): string[] {
  return items.flatMap((item) => {
    if (typeof item !== 'object' || item === null) return [];
    const entityId = (item as { entityId?: unknown }).entityId;
    return typeof entityId === 'string' && entityId !== '' ? [entityId] : [];
  });
}

async function loadAllChildren(
  type: ContainerType,
  entityId: string,
  load: ReviewStorageContentsLoader,
): Promise<string[]> {
  const expectedKind = CONTENT_KIND[type];
  if (expectedKind === undefined) return [];
  const ids: string[] = [];
  const seenPages = new Set<string>();
  let page: string | undefined;
  do {
    const result = await load(type, entityId, page);
    if (result.kind !== expectedKind) {
      throw new Error(`Unexpected ${result.kind} contents for ${type}`);
    }
    ids.push(...entityIds(result.items));
    const nextPage = result.nextPageToken;
    if (nextPage !== undefined) {
      if (seenPages.has(nextPage)) throw new Error('Storage contents pagination repeated a page');
      seenPages.add(nextPage);
    }
    page = nextPage;
  } while (page !== undefined);
  return ids;
}

/** Resolve an exact storage token to every descendant Storage_Batch id. */
export async function reviewStorageBatchIds(
  scope: ReviewStorageScope,
  load: ReviewStorageContentsLoader,
): Promise<Set<string>> {
  if (scope.type === 'batches') return new Set([scope.entityId]);

  let level: Array<{ type: ContainerType; entityId: string }> = [scope];
  const visited = new Set<string>();
  while (level.length > 0) {
    const next: Array<{ type: ContainerType; entityId: string }> = [];
    for (const node of level) {
      const visitKey = `${node.type}:${node.entityId}`;
      if (visited.has(visitKey)) continue;
      visited.add(visitKey);
      if (visited.size > MAX_STORAGE_SCOPE_ENTITIES) {
        throw new Error('Storage scope is too large to display safely');
      }
      const childType = CHILD_TYPE[node.type];
      if (childType === undefined) continue;
      const children = await loadAllChildren(node.type, node.entityId, load);
      next.push(...children.map((entityId) => ({ type: childType, entityId })));
    }
    if (next.length > 0 && next[0]!.type === 'batches') {
      return new Set(next.map((node) => node.entityId));
    }
    level = next;
  }
  return new Set();
}
